/**
 * What to tell someone when a training submission fails.
 *
 * Dataset upload and job submission are both charged to the wallet before any
 * provider work starts, and both answer 402 INSUFFICIENT_BALANCE when it cannot
 * cover them. TrainMode already recognises that on the captioning loop and
 * stops with the wallet's own message; the dialog's submit path did not, so an
 * empty wallet came out as "Training failed" with the raw code and HTTP status
 * appended — a billing problem dressed as a crash, next to a Copy error button.
 *
 * Retrying costs the same money and fails the same way, so a balance failure is
 * reported as its own thing and the run stops there.
 */
import { isApiError } from '@/lib/api/api-helpers'

export interface SubmitFailure {
  /** Headline for the error panel. */
  title: string
  /** What to show under it. Already plain English for a balance failure. */
  message: string
  /** True when the wallet, not the code, is what stopped this. */
  insufficientBalance: boolean
}

export function describeSubmitFailure(error: unknown): SubmitFailure {
  if (isApiError(error, 'INSUFFICIENT_BALANCE')) {
    return {
      title: 'Not enough credits',
      message: error.message,
      insufficientBalance: true,
    }
  }

  const err = error instanceof Error ? error : new Error(String(error))
  // Anything else keeps the code and status: those are what makes a bug report
  // from the Copy error button worth anything.
  const code = 'code' in err && err.code ? ` [${String(err.code)}]` : ''
  const status = 'status' in err && err.status ? ` (HTTP ${String(err.status)})` : ''
  return {
    title: 'Training failed',
    message: `${err.message || 'Training submission failed'}${code}${status}`,
    insufficientBalance: false,
  }
}
